import type { FC } from 'react';
import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';
import { keyframes, styled } from '@compiled/react';

import { xcss, Box, Flex } from '@atlaskit/primitives';
import { useIsReducedMotion, SlideIn, ExitingPersistence } from '@atlaskit/motion';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { SaveIndicatorContext } from './SaveIndicatorContext';
import type { SaveIndicatorComponentProps } from './types';
import { CloudSavedIcon } from './CloudSavedIcon';
import { CloudSavingIcon } from './CloudSavingIcon';
import { timeFormatter } from './timeFormatter';

// time for the status message to slide in and out
const STATUS_MESSAGE_ANIMATION_DURATION = 800;
// minimum time the status message should be shown after sliding in or changing status
const STATUS_MESSAGE_MIMINUM_SHOW_DURATION = 2000;
// time for the status icon to slide in and out
const STATUS_ICON_ANIMATION_DURATION = 800;

enum STATUSES {
	DISABLED = 'disabled',
	ERROR = 'error',
	SAVING = 'saving',
	SAVED = 'saved',
}

const i18n = defineMessages({
	saved: {
		id: 'save-indicator.save-indicator-component-experiment.saved',
		defaultMessage: 'Saved',
		description: 'Text to display when the document has been saved',
	},
	saving: {
		id: 'save-indicator.save-indicator-component-experiment.saving',
		defaultMessage: 'Saving...',
		description: 'Text to display when the document is being saved',
	},
	tooltipSaved: {
		id: 'save-indicator.save-indicator-component-experiment.tooltip-saved',
		description: 'Tooltip message of save indicator to indicate all changes are saved.',
		defaultMessage: 'Every change is saved, the last change was {time}',
	},
	tooltipError: {
		id: 'save-indicator.save-indicator-component-experiment.tooltip-error',
		description: 'Tooltip message of save indicator to indicate an error.',
		defaultMessage: 'We couldn’t save the latest changes. The last save was {time}',
	},
});

const minWidthStyle = xcss({
	minWidth: '77px',
});

const spinAnimation = keyframes({
	'0%': { transform: 'rotate(0deg)' },
	'100%': { transform: 'rotate(360deg)' },
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const SpinningWrapper = styled.div<{ isReducedMotion: boolean }>({
	display: 'flex',
	animationName: spinAnimation,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	animationDuration: ({ isReducedMotion }) => (isReducedMotion ? 0 : '1.5s'),
	animationTimingFunction: 'linear',
	animationIterationCount: 'infinite',
	animationDirection: 'reverse',
});

// atlaskit Flex does not support classNames, in order to allow for motion animations, we need to use styled.div
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const FlexWrapper = styled.div({
	display: 'flex',
});

const StatusMessage: FC<{ status: STATUSES }> = ({ status }) => {
	const message = useMemo(() => {
		switch (status) {
			case STATUSES.SAVED:
				return <FormattedMessage {...i18n.saved} />;
			case STATUSES.SAVING:
				return <FormattedMessage {...i18n.saving} />;
			default:
				return null;
		}
	}, [status]);

	return (
		<SlideIn
			enterFrom="right"
			exitTo="right"
			fade="inout"
			duration={STATUS_MESSAGE_ANIMATION_DURATION}
		>
			{(props) => {
				return <div {...props}>{message}</div>;
			}}
		</SlideIn>
	);
};

const StatusIcon: FC<{ icon: STATUSES; savedTime: Date }> = ({ icon, savedTime }) => {
	const { formatMessage } = useIntl();
	const isReducedMotion = useIsReducedMotion();
	const [tooltip, setTooltip] = useState<string>('');

	const refreshTooltip = useCallback(() => {
		if (icon === STATUSES.SAVING) {
			setTooltip('');
		} else if (icon === STATUSES.ERROR) {
			setTooltip(
				formatMessage(i18n.tooltipError, {
					time: timeFormatter(savedTime, formatMessage),
				}),
			);
		} else {
			setTooltip(
				formatMessage(i18n.tooltipSaved, {
					time: timeFormatter(savedTime, formatMessage),
				}),
			);
		}
	}, [icon, formatMessage, savedTime]);

	return (
		<Tooltip content={tooltip} position="bottom">
			<Box onMouseEnter={refreshTooltip} onFocus={refreshTooltip}>
				{icon === STATUSES.SAVED && (
					<SlideIn enterFrom="top" fade="inout" duration={STATUS_ICON_ANIMATION_DURATION}>
						{(props) => (
							<FlexWrapper {...props}>
								<CloudSavedIcon label={formatMessage(i18n.saved)} />
							</FlexWrapper>
						)}
					</SlideIn>
				)}
				{icon === STATUSES.SAVING && (
					<SlideIn enterFrom="bottom" fade="inout" duration={STATUS_ICON_ANIMATION_DURATION}>
						{(props) => {
							return (
								<div {...props}>
									<SpinningWrapper isReducedMotion={isReducedMotion}>
										<CloudSavingIcon label={formatMessage(i18n.saving)} />
									</SpinningWrapper>
								</div>
							);
						}}
					</SlideIn>
				)}
				{icon === STATUSES.ERROR && (
					<Flex>
						<WarningIcon label="Error" primaryColor={token('color.icon.subtle')} size="small" />
					</Flex>
				)}
			</Box>
		</Tooltip>
	);
};

export type SaveIndicatorComponentExperimentProps = Omit<
	SaveIndicatorComponentProps,
	'formatMessage'
>;

export const SaveIndicatorComponentExperiment: FC<SaveIndicatorComponentExperimentProps> = ({
	editorActions,
	saveError,
	title,
	hasEmoji,
	hasCoverPicture,
	selectingEmojiTitle,
	selectingCoverPicture,
}) => {
	const [status, setStatus] = useState<STATUSES>(STATUSES.DISABLED);
	const [showStatus, setShowStatus] = useState(false);
	const [icon, setIcon] = useState<STATUSES>(STATUSES.DISABLED);

	const lastSavingTime = useRef<number>(0);
	const saveStatusDelayTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const hideStatusTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const savingIconDelayTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	const { isSaving, savedTime } = useContext(SaveIndicatorContext);

	// update status
	// MODES-4696: If you type fast enough after saving -> saved state, the status will not update to saving.
	// This is made more noticeable with this experiment, but it is not a regression from this experiment.
	useEffect(() => {
		saveStatusDelayTimeout.current && clearTimeout(saveStatusDelayTimeout.current);
		if (editorActions?.isDocumentEmpty() && !title && !hasEmoji && !hasCoverPicture) {
			setStatus(STATUSES.DISABLED);
		} else if (saveError) {
			setStatus(STATUSES.ERROR);
		} else if (isSaving || selectingEmojiTitle || selectingCoverPicture) {
			lastSavingTime.current = Date.now();
			setStatus(STATUSES.SAVING);
		} else {
			// We are intentionally waiting to set the status to saved to extend the duration of the saving animation.
			// This reduces jarring animation transitions when the content is saved before the animation is complete.
			// This has no effect on actual saving, only the UI.
			const timeSinceLastSave = Date.now() - lastSavingTime.current;
			saveStatusDelayTimeout.current = setTimeout(
				() => setStatus(STATUSES.SAVED),
				timeSinceLastSave < STATUS_MESSAGE_MIMINUM_SHOW_DURATION
					? STATUS_MESSAGE_MIMINUM_SHOW_DURATION - timeSinceLastSave
					: 0,
			);
		}
	}, [
		title,
		hasEmoji,
		hasCoverPicture,
		saveError,
		isSaving,
		selectingEmojiTitle,
		selectingCoverPicture,
		editorActions,
	]);

	// status text animation logic
	useEffect(() => {
		hideStatusTimeout.current && clearTimeout(hideStatusTimeout.current);
		if (status === STATUSES.SAVING) {
			setShowStatus(true);
		} else if (status === STATUSES.SAVED) {
			hideStatusTimeout.current = setTimeout(() => {
				setShowStatus(false);
			}, STATUS_MESSAGE_MIMINUM_SHOW_DURATION);
		}
	}, [status]);

	// icon animation logic
	useEffect(() => {
		savingIconDelayTimeout.current && clearTimeout(savingIconDelayTimeout.current);
		if (status === STATUSES.SAVING) {
			savingIconDelayTimeout.current = setTimeout(
				() => setIcon(STATUSES.SAVING),
				STATUS_MESSAGE_ANIMATION_DURATION,
			);
		} else {
			setIcon(status);
		}
	}, [status]);

	// cleanup timeouts on unmount
	useEffect(() => {
		return () => {
			saveStatusDelayTimeout.current && clearTimeout(saveStatusDelayTimeout.current);
			hideStatusTimeout.current && clearTimeout(hideStatusTimeout.current);
			savingIconDelayTimeout.current && clearTimeout(savingIconDelayTimeout.current);
		};
	}, []);

	return status !== STATUSES.DISABLED ? (
		<Box paddingInlineEnd="space.100" role="status">
			<Flex xcss={minWidthStyle} gap="space.050" alignItems="center" justifyContent="end">
				<ExitingPersistence>{showStatus && <StatusMessage status={status} />}</ExitingPersistence>
				<StatusIcon icon={icon} savedTime={savedTime} />
			</Flex>
		</Box>
	) : null;
};
